import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  BoltOutlined,
  PublicOutlined,
  InsightsOutlined,
  VolunteerActivismOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii } from '../../styles/tokens';

interface TrustItem {
  icon: React.ReactElement;
  label: string;
  caption: string;
  tint: string;
}

const items: TrustItem[] = [
  {
    icon: <BoltOutlined />,
    label: 'Real-time Data',
    caption: 'Live rates, holidays, and geolocation from trusted public sources.',
    tint: colors.warning,
  },
  {
    icon: <PublicOutlined />,
    label: 'Global Coverage',
    caption: 'Country data and IBAN validation for every ISO-registered market.',
    tint: colors.primary,
  },
  {
    icon: <InsightsOutlined />,
    label: 'Expert Insights',
    caption: 'Commerce intelligence and regulatory alerts for cross-border teams.',
    tint: colors.accent,
  },
  {
    icon: <VolunteerActivismOutlined />,
    label: 'Free to Use',
    caption: 'No account, no API key - every tool is open and ready to go.',
    tint: colors.success,
  },
];

export const Footer: React.FC = () => (
  <Box
    component="footer"
    sx={{
      borderTop: `1px solid ${colors.border}`,
      bgcolor: colors.paper,
      mt: 'auto',
    }}
  >
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 5 },
        display: 'grid',
        gap: { xs: 3, md: 4 },
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: `${radii.button}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(item.tint, 0.1),
              color: item.tint,
            }}
          >
            {item.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.textPrimary }}>
              {item.label}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textSecondary, lineHeight: 1.5, display: 'block', mt: 0.25 }}>
              {item.caption}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>

    <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          © {new Date().getFullYear()} Global Cart Kit
        </Typography>

        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { label: 'Privacy', to: '/privacy' },
            { label: 'Terms', to: '/terms' },
            { label: 'Contact', to: 'mailto:hello@globalcartkit.com', external: true },
          ].map(({ label, to, external }) =>
            external ? (
              <Typography
                key={label}
                component="a"
                href={to}
                variant="caption"
                sx={{ color: colors.textMuted, textDecoration: 'none', '&:hover': { color: colors.primary } }}
              >
                {label}
              </Typography>
            ) : (
              <Typography
                key={label}
                component={Link}
                to={to}
                variant="caption"
                sx={{ color: colors.textMuted, textDecoration: 'none', '&:hover': { color: colors.primary } }}
              >
                {label}
              </Typography>
            )
          )}
        </Box>
      </Box>
    </Box>
  </Box>
);
